/**
 * Knowledge Pack detail — Phase 12 admin/governance.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  api,
  KnowledgePackAction,
  KnowledgePackDetail,
  KnowledgePackHistoryEntry,
  KnowledgePackValidationResponse,
} from '../lib/api'
import { KnowledgeControlAuthError, loadKnowledgeAdminToken, resolveKnowledgeAdminAccess } from '../lib/knowledgeControlAuth'
import { LoadingSpinner } from '../components/design-system/Loading'
import { Badge } from '../components/design-system/Badge'
import { Button } from '../components/design-system/Button'
import { Modal } from '../components/design-system/Modal'
import { EmptyState } from '../components/design-system/EmptyState'
import { useLocale } from '../contexts/LocaleContext'
import {
  actionLabel,
  formatLabel,
  lifecycleLabel,
  packDisplayName,
  validationLabel,
} from '../lib/i18n'

const CONFIRM_ACTIONS = new Set<KnowledgePackAction>([
  'approve',
  'reject',
  'revise',
  'activate',
  'deprecate',
  'submit_testing',
])

const LIFECYCLE_ACTIONS = new Set<KnowledgePackAction>([
  'validate',
  'submit_testing',
  'approve',
  'reject',
  'revise',
  'activate',
  'deprecate',
])

export function KnowledgePackDetailPage() {
  const { t, locale } = useLocale()
  const { packId } = useParams<{ packId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)
  const [access, setAccess] = useState<'pending' | 'yes' | 'no'>(() => (loadKnowledgeAdminToken() ? 'yes' : 'pending'))
  const [pack, setPack] = useState<KnowledgePackDetail | null>(null)
  const [history, setHistory] = useState<KnowledgePackHistoryEntry[]>([])
  const [validation, setValidation] = useState<KnowledgePackValidationResponse | null>(null)
  const [resolvePreview, setResolvePreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<KnowledgePackAction | null>(null)
  const [reason, setReason] = useState('')
  const [memberId, setMemberId] = useState('')
  const [newVersion, setNewVersion] = useState('')
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')

  useEffect(() => {
    if (access !== 'pending') return
    let cancelled = false
    void resolveKnowledgeAdminAccess(() => api.me()).then((ok) => {
      if (!cancelled) {
        setAccess(ok ? 'yes' : 'no')
        if (!ok) setUnauthorized(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [access])

  useEffect(() => {
    if (!packId || access !== 'yes') {
      if (access === 'no') setLoading(false)
      return
    }
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packId, access])

  async function load() {
    if (!packId) return
    try {
      setLoading(true)
      setError(null)
      const [detail, hist] = await Promise.all([
        api.getKnowledgePack(packId),
        api.getKnowledgePackHistory(packId),
      ])
      setPack(detail)
      setHistory(hist.entries)
      setEditName(detail.name)
      setEditDescription(detail.description || '')
    } catch (err) {
      if (err instanceof KnowledgeControlAuthError) {
        setUnauthorized(true)
      } else {
        setError(err instanceof Error ? err.message : t('packs.failed'))
      }
    } finally {
      setLoading(false)
    }
  }

  async function runAction(action: KnowledgePackAction, confirmedReason: string) {
    if (!packId || !pack) return
    try {
      setBusy(true)
      setActionError(null)
      if (action === 'validate') {
        const result = await api.validateKnowledgePack(packId)
        setValidation(result)
      } else if (action === 'submit_testing') {
        await api.transitionKnowledgePack(packId, {
          to_status: 'testing',
          reason: confirmedReason,
          from_status: pack.status,
          version_number: pack.version_number,
        })
      } else if (action === 'deprecate') {
        await api.transitionKnowledgePack(packId, {
          to_status: 'deprecated',
          reason: confirmedReason,
          from_status: pack.status,
          version_number: pack.version_number,
        })
      } else if (action === 'approve' || action === 'reject' || action === 'revise' || action === 'activate') {
        await api.approveKnowledgePack(packId, {
          action,
          notes: confirmedReason,
          revision_requested: action === 'revise' ? confirmedReason : undefined,
        })
      }
      setPendingAction(null)
      setReason('')
      await load()
    } catch (err) {
      if (err instanceof KnowledgeControlAuthError) {
        setUnauthorized(true)
      } else {
        setActionError(err instanceof Error ? err.message : t('kc.actionFailed'))
      }
    } finally {
      setBusy(false)
    }
  }

  async function handleAddMember() {
    if (!packId || !memberId.trim()) return
    try {
      setBusy(true)
      setActionError(null)
      await api.addKnowledgePackMember(packId, memberId.trim())
      setMemberId('')
      await load()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('kc.addMemberFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function handleRemoveMember(ruleDbId: string) {
    if (!packId) return
    try {
      setBusy(true)
      setActionError(null)
      await api.removeKnowledgePackMember(packId, ruleDbId)
      await load()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('kc.removeMemberFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function handleSaveEdit() {
    if (!packId) return
    try {
      setBusy(true)
      setActionError(null)
      await api.updateKnowledgePack(packId, {
        name: editName.trim(),
        description: editDescription,
        version_number: pack?.version_number,
      })
      await load()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('kc.updateFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function handleCreateVersion() {
    if (!packId || !newVersion.trim()) return
    try {
      setBusy(true)
      setActionError(null)
      const created = await api.createKnowledgePackVersion(packId, {
        version: newVersion.trim(),
        reason: `Create version ${newVersion.trim()} from ${pack?.version}`,
      })
      navigate(`/knowledge-control/packs/${created.id}`)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('kc.versionFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function handleResolvePreview() {
    if (!pack) return
    try {
      setBusy(true)
      setActionError(null)
      const result = await api.resolveKnowledgePacks({
        packs: [{ pack_id: pack.pack_id, version: pack.version }],
      })
      setResolvePreview(`${result.mode}: ${result.total_rules} ACTIVE rules, hash ${result.ruleset_hash.slice(0, 12)}`)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('kc.resolveFailed'))
    } finally {
      setBusy(false)
    }
  }

  if (unauthorized) {
    return (
      <div style={{ padding: '32px' }} data-testid="knowledge-packs-unauthorized">
        <EmptyState title={t('kc.authRequired')} description={t('packs.tokenLead')} />
        <Button onClick={() => navigate('/knowledge-control/packs')} variant="primary">
          {t('kc.openPacks')}
        </Button>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', minHeight: '400px', alignItems: 'center' }}>
        <LoadingSpinner size="lg" text={t('packs.loading')} />
      </div>
    )
  }

  if (error || !pack) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '32px' }}>
        <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', color: 'var(--reviewer-danger)' }}>
          {error || t('packs.notFound')}
        </h2>
        <Button onClick={() => navigate('/knowledge-control/packs')} variant="secondary">
          {t('packs.backControl')}
        </Button>
      </div>
    )
  }

  const lifecycleActions = pack.legal_actions.filter((action) => LIFECYCLE_ACTIONS.has(action))

  return (
    <div style={{ padding: '24px' }} data-testid="knowledge-pack-detail">
      <div style={{ marginBottom: '16px', fontFamily: 'var(--reviewer-font-mono)', fontSize: '12px' }}>
        <Link to="/knowledge-control/packs" style={{ color: 'var(--reviewer-primary)', textDecoration: 'none' }}>
          {t('nav.knowledgePacks')}
        </Link>
        <span> / {pack.pack_id}</span>
      </div>

      <h1 style={{ fontFamily: 'var(--reviewer-font-mono)', marginTop: 0 }}>
        {packDisplayName(locale, pack.pack_id, pack.name)}
      </h1>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <Badge variant="warning">{lifecycleLabel(locale, pack.status)}</Badge>
        <Badge variant={pack.validation_status === 'pass' ? 'success' : 'default'}>
          {t('packs.colValidation')} {validationLabel(locale, pack.validation_status)}
        </Badge>
        <Badge variant="default">{formatLabel(locale, pack.format_type)}</Badge>
        <Badge variant="info">{pack.pack_id}@{pack.version}</Badge>
      </div>
      <p style={{ fontFamily: 'var(--reviewer-font-mono)', maxWidth: '720px' }}>{pack.description}</p>

      {pack.legal_actions.includes('edit') && (
        <section data-testid="knowledge-pack-edit" style={{ marginTop: '16px', maxWidth: '720px' }}>
          <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: '16px' }}>{t('packs.editMeta')}</h2>
          <label style={{ display: 'block', fontFamily: 'var(--reviewer-font-mono)', marginBottom: '8px' }}>
            {t('packs.fieldName')}
            <input
              value={editName}
              onChange={(event) => setEditName(event.target.value)}
              data-testid="knowledge-pack-edit-name"
              style={{
                display: 'block',
                width: '100%',
                marginTop: '4px',
                padding: '8px',
                border: '1px solid var(--reviewer-border)',
                background: 'var(--reviewer-bg-primary)',
                color: 'var(--reviewer-text)',
                fontFamily: 'var(--reviewer-font-mono)',
              }}
            />
          </label>
          <label style={{ display: 'block', fontFamily: 'var(--reviewer-font-mono)', marginBottom: '8px' }}>
            {t('packs.fieldDescription')}
            <textarea
              value={editDescription}
              onChange={(event) => setEditDescription(event.target.value)}
              data-testid="knowledge-pack-edit-description"
              style={{
                display: 'block',
                width: '100%',
                minHeight: '72px',
                marginTop: '4px',
                padding: '8px',
                border: '1px solid var(--reviewer-border)',
                background: 'var(--reviewer-bg-primary)',
                color: 'var(--reviewer-text)',
                fontFamily: 'var(--reviewer-font-mono)',
              }}
            />
          </label>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || !editName.trim()}
            onClick={() => void handleSaveEdit()}
          >
            {t('packs.saveMeta')}
          </Button>
        </section>
      )}

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '16px 0' }}>
        {lifecycleActions.map((action) => (
          <Button
            key={action}
            size="sm"
            variant={action === 'activate' || action === 'reject' ? 'primary' : 'secondary'}
            disabled={busy}
            data-testid={`knowledge-pack-action-${action}`}
            onClick={() => {
              if (action === 'validate') {
                void runAction(action, 'validate')
                return
              }
              if (CONFIRM_ACTIONS.has(action)) {
                setPendingAction(action)
                setReason('')
              }
            }}
          >
            {actionLabel(locale, action)}
          </Button>
        ))}
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => void handleResolvePreview()}>
          {t('packs.previewResolve')}
        </Button>
      </div>
      {actionError && (
        <p data-testid="knowledge-pack-action-error" style={{ color: 'var(--reviewer-danger)', fontFamily: 'var(--reviewer-font-mono)' }}>
          {actionError}
        </p>
      )}
      {validation && (
        <p data-testid="knowledge-pack-validation" style={{ fontFamily: 'var(--reviewer-font-mono)' }}>
          {t('packs.colValidation')} {validationLabel(locale, validation.overall_status)}; {t('packs.canActivate')}: {String(validation.can_activate)};
          {t('packs.membersCount')} {validation.member_count}; {t('packs.nonActive')} {validation.non_active_members}
        </p>
      )}
      {resolvePreview && (
        <p data-testid="knowledge-pack-resolve-preview" style={{ fontFamily: 'var(--reviewer-font-mono)' }}>
          {resolvePreview}
        </p>
      )}

      <section style={{ marginTop: '24px' }}>
        <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: '16px' }}>{t('packs.members')}</h2>
        {pack.membership_mutable && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input
              value={memberId}
              onChange={(event) => setMemberId(event.target.value)}
              placeholder={t('packs.memberId')}
              data-testid="knowledge-pack-member-id"
              style={{
                flex: 1,
                padding: '8px',
                border: '1px solid var(--reviewer-border)',
                fontFamily: 'var(--reviewer-font-mono)',
                background: 'var(--reviewer-bg-primary)',
                color: 'var(--reviewer-text)',
              }}
            />
            <Button size="sm" disabled={busy || !memberId.trim()} onClick={() => void handleAddMember()}>
              {t('packs.addMember')}
            </Button>
          </div>
        )}
        {pack.members.length === 0 ? (
          <EmptyState title={t('packs.noMembers')} description={t('packs.noMembersBody')} />
        ) : (
          <table data-testid="knowledge-pack-members" style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--reviewer-font-mono)' }}>
            <thead>
              <tr>
                {[t('kc.colRule'), t('packs.colStatus'), t('packs.colActive'), ''].map((header) => (
                  <th key={header} style={{ textAlign: 'left', borderBottom: '1px solid var(--reviewer-border)', padding: '8px' }}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pack.members.map((member) => (
                <tr key={member.id}>
                  <td style={{ padding: '8px' }}>
                    {member.title}
                    <div style={{ fontSize: '11px' }}>{member.rule_id}@{member.version}</div>
                  </td>
                  <td style={{ padding: '8px' }}>{lifecycleLabel(locale, member.status)}</td>
                  <td style={{ padding: '8px' }}>{member.active ? t('packs.yes') : t('packs.no')}</td>
                  <td style={{ padding: '8px' }}>
                    {pack.membership_mutable && (
                      <Button size="sm" variant="ghost" onClick={() => void handleRemoveMember(member.id)}>
                        {t('packs.removeMember')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={{ marginTop: '24px' }}>
        <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: '16px' }}>{t('packs.newVersion')}</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            value={newVersion}
            onChange={(event) => setNewVersion(event.target.value)}
            placeholder="1.1.0"
            data-testid="knowledge-pack-new-version"
            style={{
              padding: '8px',
              border: '1px solid var(--reviewer-border)',
              fontFamily: 'var(--reviewer-font-mono)',
              background: 'var(--reviewer-bg-primary)',
              color: 'var(--reviewer-text)',
            }}
          />
          <Button size="sm" disabled={busy || !newVersion.trim()} onClick={() => void handleCreateVersion()}>
            {t('packs.copyDraft')}
          </Button>
        </div>
      </section>

      <section data-testid="knowledge-pack-history" style={{ marginTop: '24px' }}>
        <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: '16px' }}>{t('packs.history')}</h2>
        {history.length === 0 ? (
          <p style={{ fontFamily: 'var(--reviewer-font-mono)', color: 'var(--reviewer-text-secondary)' }}>{t('packs.noHistory')}</p>
        ) : (
          <ul>
            {history.map((entry) => (
              <li key={entry.id} style={{ fontFamily: 'var(--reviewer-font-mono)', marginBottom: '8px' }}>
                {lifecycleLabel(locale, entry.from_status || 'draft')} → {lifecycleLabel(locale, entry.to_status)} {entry.actor_name || '—'}: {entry.reason}
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        title={pendingAction ? actionLabel(locale, pendingAction) : t('act.confirm')}
      >
        <div style={{ fontFamily: 'var(--reviewer-font-mono)' }}>
          <p>{t('packs.confirmBody')}</p>
          <textarea
            data-testid="knowledge-pack-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('act.reason')}
            style={{
              width: '100%',
              minHeight: '80px',
              margin: '12px 0',
              padding: '8px',
              border: '1px solid var(--reviewer-border)',
              fontFamily: 'var(--reviewer-font-mono)',
              background: 'var(--reviewer-bg-primary)',
              color: 'var(--reviewer-text)',
            }}
          />
          <Button
            variant="primary"
            disabled={!reason.trim() || busy}
            onClick={() => pendingAction && void runAction(pendingAction, reason.trim())}
          >
            {t('act.confirm')}
          </Button>
        </div>
      </Modal>
    </div>
  )
}
